// Libraries
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
// Actions
import ShowNotification from '../../actions/notification';
// Services
import { getPatientConsentStatus } from '../../services/patient';
// Constants
import { NOTIFICATION_TYPE } from '../../constants/constants';

const useLoadPatientConsentStatus = ({ patientId }) => {
  const [consentStatus, setConsentStatus] = useState(null);
  // Distinguishes "fetch still in flight" from a settled NONE result so the UI can show a loading
  // placeholder rather than conflating the two (both would otherwise look like an empty status).
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const showNotification = (notificationData) => dispatch(ShowNotification(notificationData));

  // Returns the cancelable request so callers (and the effect below) can abort an in-flight fetch.
  const fetchConsentStatus = useCallback(() => {
    const request = getPatientConsentStatus(patientId);
    request.promise
      .then((data) => {
        setConsentStatus(data);
        setLoading(false);
      })
      .catch((error) => {
        // A canceled request (patient changed / unmounted) must not touch state or notify; the next
        // effect run owns the loading flag for the new patient.
        if (error.isCanceled) {
          return;
        }
        setLoading(false);
        if (error.status === 401 || error.status === 403) {
          return;
        }
        showNotification({
          message: 'An error occurred while attempting to load consent status.',
          autoHide: true,
          notificationType: NOTIFICATION_TYPE.ERROR,
        });
      });
    return request;
  }, [patientId]);

  useEffect(() => {
    // Clear any prior patient's status so a stale value never shows while the new fetch is in flight.
    setConsentStatus(null);
    setLoading(true);
    const request = fetchConsentStatus();
    return () => request.cancel();
  }, [fetchConsentStatus]);

  return { consentStatus, loading, refetch: fetchConsentStatus };
};

export default useLoadPatientConsentStatus;
